import { Fragment, ReactNode, useState } from "react";
import { useNotification, useTranslate } from "@refinedev/core";
import { Flex, GlobalToken, Typography } from "antd";
import styled from "styled-components";
import {
  ChatCircleDotsIcon,
  GraduationCapIcon,
  LightbulbIcon,
  MapTrifoldIcon,
  SlidersHorizontalIcon,
  WaveformIcon,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { Divider, Eyebrow } from "@libertify/ui";
import { useAntTheme } from "hooks/useAntTheme";
import type { TeacherProfile } from "pages/creator-profile/types";
import {
  ILLUSTRATION_OPTIONS,
  STRUCTURE_OPTIONS,
  VOICE1_OPTION_KEY,
  VOICE1_OPTIONS,
  VOICE2_OPTIONS,
} from "pages/creator-profile/dnaOptions";
import {
  answersToDna,
  dnaToAnswers,
  isSameAnswers,
} from "pages/creator-profile/dnaAnswers";
import type { DnaAnswers } from "pages/creator-profile/dnaAnswers";
import { useUpdateCreatorProfile } from "pages/creator-profile/hooks/useUpdateCreatorProfile";
import { getDnaArtwork, getDnaCopyKey } from "../creatorDnaArchetypes";
import { CreatorDnaModalFooter } from "./CreatorDnaModalFooter";
import { DnaChoiceRow } from "./DnaChoiceRow";
import { DnaEditSection } from "./DnaEditSection";
import { DnaSlider } from "./DnaSlider";

// Questions and options reuse the onboarding wizard's copy, so editing reads
// exactly like the questionnaire the user answered.
const ONBOARDING_COPY = "pages.teacherOnboarding";

export type CreatorDnaEditProps = Readonly<{
  profile: TeacherProfile;
  onClose: () => void;
  onSaved: () => void;
}>;

export const CreatorDnaEdit = ({
  profile,
  onClose,
  onSaved,
}: CreatorDnaEditProps) => {
  const t = useTranslate();
  const { open } = useNotification();
  const { theme } = useAntTheme();
  const { mutateAsync, isLoading: isSaving } = useUpdateCreatorProfile();

  const [initial] = useState(() => dnaToAnswers(profile.dna));
  const [answers, setAnswers] = useState(initial);

  const setAnswer = (patch: Partial<DnaAnswers>) =>
    setAnswers((curr) => ({ ...curr, ...patch }));

  const canSave =
    answers.voice2 !== null && !isSameAnswers(answers, initial) && !isSaving;

  const handleSave = async () => {
    try {
      await mutateAsync({
        profileId: profile.id,
        values: { dna: answersToDna(answers, initial, profile.dna) },
      });
      onSaved();
    } catch {
      open?.({
        type: "error",
        message: t("pages.user.edit.creatorDna.saveError"),
      });
    }
  };

  const option = (base: string, key: string) => ({
    label: t(`${ONBOARDING_COPY}.${base}.options.${key}.label`),
    description: t(`${ONBOARDING_COPY}.${base}.options.${key}.quote`),
  });

  const sliderEnds = (base: string) => ({
    low: {
      label: t(`${ONBOARDING_COPY}.${base}.low.label`),
      quote: t(`${ONBOARDING_COPY}.${base}.low.quote`),
    },
    high: {
      label: t(`${ONBOARDING_COPY}.${base}.high.label`),
      quote: t(`${ONBOARDING_COPY}.${base}.high.quote`),
    },
  });

  const sections: {
    key: string;
    base: string;
    icon: Icon;
    field: ReactNode;
  }[] = [
    {
      key: "illustration",
      base: "illustration",
      icon: LightbulbIcon,
      field: (
        <DnaChoiceRow
          options={ILLUSTRATION_OPTIONS.map((value) => ({
            value,
            ...option("illustration", value),
          }))}
          selected={answers.illustration}
          onSelect={(illustration) => setAnswer({ illustration })}
        />
      ),
    },
    {
      key: "structure",
      base: "structure",
      icon: MapTrifoldIcon,
      field: (
        <DnaChoiceRow
          options={STRUCTURE_OPTIONS.map((value) => ({
            value,
            ...option("structure", value),
          }))}
          selected={answers.structure}
          onSelect={(structure) => setAnswer({ structure })}
        />
      ),
    },
    {
      key: "voice1",
      base: "voice",
      icon: WaveformIcon,
      field: (
        <DnaChoiceRow
          options={VOICE1_OPTIONS.map((value) => ({
            value,
            ...option("voice", VOICE1_OPTION_KEY[value]),
          }))}
          selected={answers.voice1}
          onSelect={(voice1) => {
            // The second voice pick branches off the first, so it resets.
            if (voice1 !== answers.voice1) {
              setAnswer({ voice1, voice2: null });
            }
          }}
        />
      ),
    },
    {
      key: "voice2",
      base: "voice2",
      icon: SlidersHorizontalIcon,
      field: (
        <DnaChoiceRow
          options={VOICE2_OPTIONS[answers.voice1].map((value) => ({
            value,
            ...option("voice2", value),
          }))}
          selected={answers.voice2}
          onSelect={(voice2) => setAnswer({ voice2 })}
        />
      ),
    },
    {
      key: "depth",
      base: "depth",
      icon: GraduationCapIcon,
      field: (
        <DnaSlider
          value={answers.depth}
          {...sliderEnds("depth")}
          onChange={(depth) => setAnswer({ depth })}
        />
      ),
    },
    {
      key: "autonomy",
      base: "autonomy",
      icon: ChatCircleDotsIcon,
      field: (
        <DnaSlider
          value={answers.autonomy}
          {...sliderEnds("autonomy")}
          onChange={(autonomy) => setAnswer({ autonomy })}
        />
      ),
    },
  ];

  const archetype = profile.archetype;
  const isKnown = !!archetype && !!getDnaArtwork(archetype.name);

  return (
    <Flex vertical>
      <StyledScrollArea $token={theme} vertical gap={theme.marginLG}>
        {archetype ? (
          <Flex vertical gap={4}>
            <Eyebrow size="s">
              {t("pages.user.edit.creatorDna.editEyebrow")}
            </Eyebrow>
            <Typography.Title level={3} style={{ margin: 0, fontWeight: 700 }}>
              {isKnown
                ? t(getDnaCopyKey(archetype.name, "name"))
                : archetype.name}
            </Typography.Title>
          </Flex>
        ) : null}

        {sections.map(({ key, base, icon, field }, index) => (
          <Fragment key={key}>
            {index > 0 ? <Divider spacing="none" /> : null}
            <DnaEditSection
              icon={icon}
              question={t(`${ONBOARDING_COPY}.${base}.question`)}
              title={t(`${ONBOARDING_COPY}.${base}.kicker`)}
            >
              {field}
            </DnaEditSection>
          </Fragment>
        ))}
      </StyledScrollArea>

      <StyledFooter $token={theme}>
        <CreatorDnaModalFooter
          closeLabel={t("buttons.cancel")}
          actionLabel={t("pages.user.edit.creatorDna.recalculate")}
          onClose={onClose}
          onAction={() => void handleSave()}
          actionDisabled={!canSave}
          actionLoading={isSaving}
        />
      </StyledFooter>
    </Flex>
  );
};

// ─── Styled components ───

// Same height as the details view, so switching modes doesn't resize the modal.
const StyledScrollArea = styled(Flex)<{ $token: GlobalToken }>`
  height: min(720px, calc(100vh - 220px));
  overflow-y: auto;
  padding: ${({ $token }) => $token.paddingLG}px;
`;

// Mirrors the Modal's own footer bar; edit renders it here because the form
// state it needs lives in this component.
const StyledFooter = styled.div<{ $token: GlobalToken }>`
  padding: ${({ $token }) => `${$token.padding}px ${$token.paddingLG}px`};
  border-top: ${({ $token }) =>
    `${$token.lineWidth}px solid ${$token.colorBorderSecondary}`};
  background: ${({ $token }) => $token.colorBgLayout};
`;
